import React, {useState, useRef, useEffect} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {useFormik} from 'formik'
import {IMenuFormValues, IMenuModel, ICON_OPTIONS, initialMenuFormValues, validationMenu} from './MenuModel'

const IconSelect: React.FC<{
  value: string
  onChange: (value: string) => void
  options: {value: string; label: string}[]
}> = ({value, onChange, options}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const selected = options.find((o) => o.value === value)

  return (
    <div className='col-md-6' ref={ref} style={{position: 'relative'}}>
      <label className='form-label fw-bold'>İkon</label>
      <div
        className='form-select d-flex align-items-center cursor-pointer'
        onClick={() => setOpen((v) => !v)}
      >
        {selected?.value ? (
          <>
            <i className={`bi ${selected.value} me-2 text-primary`} />
            {selected.label}
          </>
        ) : (
          <span className='text-muted'>İkon Seçiniz</span>
        )}
      </div>
      {open && (
        <div
          className='border rounded bg-white shadow-sm'
          style={{
            position: 'absolute',
            zIndex: 1050,
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`d-flex align-items-center px-3 py-2 cursor-pointer ${
                opt.value === value ? 'bg-light-primary' : ''
              }`}
              style={{cursor: 'pointer'}}
              onClick={() => {
                onChange(opt.value)
                setOpen(false)
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f8fa')}
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor =
                  opt.value === value ? '#e8f0fe' : 'transparent')
              }
            >
              {opt.value && <i className={`bi ${opt.value} me-2 text-primary fs-5`} />}
              <span>{opt.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface MenuFormModalProps {
  show: boolean
  editingItem: IMenuModel | null
  parentItem: IMenuModel | null
  parentOptions: {value: string; label: string}[]
  /** Kök menünün bağlanacağı ağaç seçenekleri */
  treeOptions: {value: string; label: string}[]
  /** Yeni menü bu ağaca eklenir (ekranda seçili ağaç) */
  defaultTreeId: string | null
  loading: boolean
  onClose: () => void
  onSubmit: (values: IMenuFormValues) => void
}

const MenuFormModal: React.FC<MenuFormModalProps> = ({
  show,
  editingItem,
  parentItem,
  parentOptions,
  treeOptions,
  defaultTreeId,
  loading,
  onClose,
  onSubmit,
}) => {
  const isEditing = !!editingItem

  const formik = useFormik<IMenuFormValues>({
    enableReinitialize: true,
    initialValues: editingItem
      ? {
          id: editingItem.id ?? undefined,
          title: editingItem.title,
          icon: editingItem.icon || '',
          path: editingItem.path,
          parentId: editingItem.parentId || null,
          isActive: editingItem.isActive,
          componentName: editingItem.componentName || '',
          menuTreeId: editingItem.menuTreeId || null,
        }
      : {
          ...initialMenuFormValues,
          parentId: parentItem?.id || null,
          menuTreeId: defaultTreeId,
        },
    validationSchema: validationMenu,
    onSubmit: (values) => {
      onSubmit(values)
    },
  })

  const handleClose = () => {
    formik.resetForm()
    onClose()
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName='modal-dialog-centered modal-lg'
      backdrop='static'
    >
      <form onSubmit={formik.handleSubmit}>
        <div className='modal-header'>
          <h5 className='modal-title'>
            {isEditing ? 'Menü Düzenle' : 'Yeni Menü Ekle'}
          </h5>
          <button type='button' className='btn-close' onClick={handleClose} />
        </div>

        <div className='modal-body'>
          {/* Menü Adı */}
          <div className='row mb-5'>
            <div className='col-12'>
              <label className='form-label fw-bold required'>Menü Adı</label>
              <input
                type='text'
                className={`form-control ${
                  formik.touched.title && formik.errors.title ? 'is-invalid' : ''
                }`}
                placeholder='Menü adı giriniz'
                {...formik.getFieldProps('title')}
              />
              {formik.touched.title && formik.errors.title && (
                <div className='invalid-feedback'>{formik.errors.title}</div>
              )}
            </div>
          </div>

          {/* Yol (Path) */}
          <div className='row mb-5'>
            <div className='col-12'>
              <label className='form-label fw-bold required'>Yol (Path)</label>
              <input
                type='text'
                className={`form-control ${
                  formik.touched.path && formik.errors.path ? 'is-invalid' : ''
                }`}
                placeholder='/ornek/sayfa'
                {...formik.getFieldProps('path')}
              />
              {formik.touched.path && formik.errors.path && (
                <div className='invalid-feedback'>{formik.errors.path}</div>
              )}
            </div>
          </div>

          {/* Menü ağacı: yalnızca kök menüde seçilir */}
          {!formik.values.parentId && (
            <div className='row mb-5'>
              <div className='col-12'>
                <label className='form-label fw-bold required'>Menü Ağacı</label>
                <select
                  className='form-select'
                  value={formik.values.menuTreeId || ''}
                  onChange={(e) => formik.setFieldValue('menuTreeId', e.target.value || null)}
                >
                  <option value=''>Ağaç seçiniz</option>
                  {treeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div className='form-text'>
                  Ağacı değiştirilen menü, alt menüleriyle birlikte seçilen ağaca taşınır.
                </div>
              </div>
            </div>
          )}

          {/* İkon ve Üst Menü */}
          <div className='row mb-5'>
            <IconSelect
              value={formik.values.icon}
              onChange={(val) => formik.setFieldValue('icon', val)}
              options={ICON_OPTIONS}
            />

            <div className='col-md-6'>
              <label className='form-label fw-bold'>Üst Menü</label>
              <select
                className='form-select'
                value={formik.values.parentId || ''}
                onChange={(e) => formik.setFieldValue('parentId', e.target.value || null)}
              >
                <option value=''>Ana Menü (Kök)</option>
                {parentOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Component adı (PrivateRoutes COMPONENT_REGISTRY ile eşleşmeli) */}
          <div className='row mb-5'>
            <div className='col-12'>
              <label className='form-label fw-bold'>Component</label>
              <input
                type='text'
                className='form-control'
                placeholder='Örn: CarPage'
                {...formik.getFieldProps('componentName')}
              />
              <div className='form-text text-muted'>
                Alt menüler (parent) için boş bırakılabilir. Kayıtlı bileşen adıyla aynı olmalıdır.
              </div>
            </div>
          </div>

          {/* Aktif/Pasif */}
          <div className='row mb-5'>
            <div className='col-12'>
              <div className='form-check form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='isActiveSwitch'
                  checked={formik.values.isActive}
                  onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
                />
                <label className='form-check-label fw-bold' htmlFor='isActiveSwitch'>
                  {formik.values.isActive ? 'Aktif' : 'Pasif'}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className='modal-footer'>
          <button type='button' className='btn btn-light' onClick={handleClose}>
            İptal
          </button>
          <button type='submit' className='btn btn-primary' disabled={loading}>
            {loading ? (
              <span className='indicator-progress' style={{display: 'block'}}>
                Lütfen bekleyin...
                <span className='spinner-border spinner-border-sm align-middle ms-2' />
              </span>
            ) : isEditing ? (
              'Güncelle'
            ) : (
              'Kaydet'
            )}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default MenuFormModal
